import React, { useEffect, useState } from "react";

import LaunchIcon from "@mui/icons-material/Launch";
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";

import { ButtonVariant } from "Constant/button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import { useSelector } from "react-redux";

import useHostFooterAction from "components/organisms/CreationModal/hooks/useHostFooterAction";

import "../../css/createSourceModal.css";
import {
    appsflyerMapping,
    sendAppsflyerData,
} from "../../helpers/Sources/appsflyer/appsflyer";
import { usageSourceCode } from "../../helpers/Sources/createSource";
import StandartButton from "../atoms/StandartButton";
import SwalAlertComp from "../SwalAlert";
import MmpPlatformSelector from "./MmpPlatformSelector";

// Default mapping for Appsflyer
const APPSFLYER_DEFAULT_MAPPING = {
    mappings: {
        event_time: "event_time",
        fbc: "af_sub2",
        fbp: "af_sub3",
        ua: "af_sub4",
        cn: "country_code",
        ip: "ip",
        external_id: "appsflyer_id",
        url: "original_url",
        et: "event_name",
    },
};

const EMPTY_PLATFORM_INPUT = { name: "", app_id: "" };

const AppIdTakenModal = ({ isOpen, setIsOpen }) => {
    return (
        <Dialog
            open={isOpen}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title" textAlign="center">
                {"Failed"}
            </DialogTitle>
            <DialogContent>
                <DialogContentText
                    id="alert-dialog-description"
                    textAlign="center"
                >
                    <div>
                        APP ID is not eligable,{" "}
                        <a
                            href="mailto:support@signalsight.io"
                            title="mailto:support@signalsight.io"
                            style={{
                                color: "#388BFF",
                            }}
                        >
                            <strong>please contact us.</strong>
                        </a>
                    </div>
                </DialogContentText>
            </DialogContent>
            <DialogActions
                sx={{
                    display: "flex",
                    justifyContent: "center",
                }}
                id="appsflyer-error-dialog"
            >
                <Button
                    onClick={setIsOpen}
                    autoFocus
                    color="primary"
                    variant="contained"
                >
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const AppsFlyerSource = (props) => {
    const { useHostFooter = false, registerFooterAction } = props;
    const [selectedPlatforms, setSelectedPlatforms] = useState([]);
    const [platformInputs, setPlatformInputs] = useState({
        android: { ...EMPTY_PLATFORM_INPUT },
        ios: { ...EMPTY_PLATFORM_INPUT },
        windows: { ...EMPTY_PLATFORM_INPUT },
    });
    const [isSaveLoading, setIsSaveLoading] = useState(false);
    const [connectionMethod, setConnectionMethod] =
        useState("partnerConnection");
    const [apiKey, setApiKey] = useState("");
    const [apiKeyError, setApiKeyError] = useState("");
    const [platformError, setPlatformError] = useState("");
    const [fieldErrors, setFieldErrors] = useState({
        android: { name: "", app_id: "" },
        ios: { name: "", app_id: "" },
        windows: { name: "", app_id: "" },
    });
    const [isAppIdTakenModalOpen, setIsAppIdTakenModalOpen] = useState(false);
    const [usage, setUsage] = useState();

    const language = useSelector((state) => state.languagesState.languages);
    const cannotBeBlank =
        language?.tracker_wizard?.cannot_be_blank ?? "Cannot be blank!";

    const setLoadingState = (loading) => {
        setIsSaveLoading(loading);
        props?.setLoading?.(loading);
    };

    const handleCloseModal = () => {
        props?.handleClose();
    };

    const handlePlatformInputChange = (platform, field, value) => {
        setPlatformInputs((prev) => ({
            ...prev,
            [platform]: { ...prev[platform], [field]: value },
        }));
        setFieldErrors((prev) => ({
            ...prev,
            [platform]: {
                ...prev[platform],
                [field]: value ? "" : cannotBeBlank,
            },
        }));
    };

    const validate = () => {
        let valid = true;

        if (selectedPlatforms.length === 0) {
            setPlatformError(cannotBeBlank);
            valid = false;
        } else {
            setPlatformError("");
        }

        if (!apiKey && connectionMethod === "apiKeyConnection") {
            setApiKeyError(cannotBeBlank);
            valid = false;
        } else {
            setApiKeyError("");
        }

        const newFieldErrors = {
            android: { name: "", app_id: "" },
            ios: { name: "", app_id: "" },
            windows: { name: "", app_id: "" },
        };

        selectedPlatforms.forEach((platform) => {
            const input = platformInputs[platform];
            if (!input.name) {
                newFieldErrors[platform].name = cannotBeBlank;
                valid = false;
            }
            if (!input.app_id) {
                newFieldErrors[platform].app_id = cannotBeBlank;
                valid = false;
            }
        });

        setFieldErrors(newFieldErrors);
        return valid;
    };

    const fetchAppsflyerData = async () => {
        if (!validate()) return;

        setLoadingState(true);

        const createdSourceIds = [];
        const errors = [];

        try {
            for (const platform of selectedPlatforms) {
                const input = platformInputs[platform];
                const data = {
                    name: input.name,
                    ...(connectionMethod === "apiKeyConnection"
                        ? {
                              marketplace_installed: false,
                              api_key: apiKey,
                          }
                        : {
                              marketplace_installed: true,
                          }),
                    app_id: input.app_id,
                    platform,
                    usage_types: usage,
                };

                const response = await sendAppsflyerData(data);

                if (response?.data?.app_taken) {
                    setIsAppIdTakenModalOpen(true);
                    errors.push(`${platform}: APP ID is not eligible`);
                    continue;
                }

                if (response.status) {
                    const sourceId = response?.data?.source?.id;
                    const sourceTypeId = response?.data?.source?.source_type_id;

                    if (props.selectFileSourceId) {
                        props.selectFileSourceId(sourceId, sourceTypeId);
                    }

                    const mappingResponse = await appsflyerMapping(
                        APPSFLYER_DEFAULT_MAPPING,
                        sourceId,
                    );

                    if (mappingResponse.status) {
                        createdSourceIds.push(sourceId);
                        if (props?.setIsMappingSuccessfull) {
                            props?.setIsMappingSuccessfull(true);
                        }
                    } else {
                        errors.push(
                            `${platform}: ${mappingResponse?.message ?? "Failed to save mapping"}`,
                        );
                    }
                } else {
                    errors.push(
                        `${platform}: ${response?.message ?? "Failed to create source"}`,
                    );
                }
            }

            if (errors.length > 0 && !isAppIdTakenModalOpen) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: errors.join("\n"),
                    icon: "error",
                });
            }

            if (createdSourceIds.length > 0) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: "Source created successfully",
                    icon: "success",
                    timer: 3000,
                });

                if (props.getAllSourcesReduxProps) {
                    props.getAllSourcesReduxProps();
                }
                if (props.setNextCheck) {
                    props.setNextCheck(true);
                }
                props?.handleClose(
                    createdSourceIds.length === 1
                        ? createdSourceIds[0]
                        : createdSourceIds[createdSourceIds.length - 1],
                );
                if (props.handleGetSources) {
                    props.handleGetSources();
                }
                if (props.getTracker) {
                    await props.addToTracker(
                        createdSourceIds.length === 1
                            ? createdSourceIds[0]
                            : createdSourceIds,
                    );
                }
            }
        } finally {
            setLoadingState(false);
        }
    };

    useEffect(() => {
        let fetch = async () => {
            let response = await usageSourceCode("appsflyer");
            if (response?.status) {
                const tempNameOptions = response?.data?.map((item) => ({
                    value: item?.code,
                    label: item?.name,
                    id: item?.id,
                }));
                const defaultUsage = tempNameOptions?.filter(
                    (option) => option?.label === "App",
                )[0]?.id;
                setUsage([defaultUsage]);
            }
        };
        fetch();
    }, []);

    const platformLabels = {
        android: "Android",
        ios: "iOS",
        windows: "Windows",
    };

    useHostFooterAction(registerFooterAction, "form", {
        label: language?.buttons?.create_source ?? "Create source",
        onSubmit: fetchAppsflyerData,
        isLoading: isSaveLoading,
    });

    const saveButton = (
        <StandartButton
            variant="contained"
            color="primary"
            onClick={fetchAppsflyerData}
            isLoading={isSaveLoading}
            wrapperSx={{ marginLeft: "auto", width: "fit-content" }}
            data-cy="create-source-mmp-submit"
        >
            {language?.buttons?.create_source ?? "Create Source"}
        </StandartButton>
    );

    return (
        <div className="appsflyer-create-modal">
            <Form.Group
                className="mb-3"
                controlId={"exampleForm.ControlInput1"}
            >
                {isAppIdTakenModalOpen && (
                    <AppIdTakenModal
                        isOpen={!!isAppIdTakenModalOpen}
                        setIsOpen={() => setIsAppIdTakenModalOpen(false)}
                    />
                )}

                <MmpPlatformSelector
                    selectedPlatforms={selectedPlatforms}
                    onChange={(platforms) => {
                        setSelectedPlatforms(platforms);
                        if (platforms.length > 0) setPlatformError("");
                    }}
                    disabled={isSaveLoading}
                    error={platformError}
                    label={language?.source_modal?.platform ?? "Platform"}
                />

                {selectedPlatforms.map((platform) => (
                    <div key={platform} style={{ marginBottom: "16px" }}>
                        {selectedPlatforms.length > 1 && (
                            <Typography
                                variant="subtitle2"
                                style={{
                                    fontWeight: 600,
                                    marginBottom: "8px",
                                    textTransform: "capitalize",
                                }}
                            >
                                {platformLabels[platform]}
                            </Typography>
                        )}

                        <FormControl className="mb-3" style={{ width: "100%" }}>
                            <TextField
                                label={language?.source_modal?.name ?? "Name"}
                                variant="outlined"
                                onChange={(e) =>
                                    handlePlatformInputChange(
                                        platform,
                                        "name",
                                        e.target.value,
                                    )
                                }
                                placeholder={
                                    language?.source_modal?.please_write_name ??
                                    "Please Write Name"
                                }
                                value={platformInputs[platform].name}
                                required
                                disabled={isSaveLoading}
                                inputProps={{ "data-cy": "mmp-app-name" }}
                            />
                            {fieldErrors[platform]?.name && (
                                <div className="fileUploadStyle">
                                    {fieldErrors[platform].name}
                                </div>
                            )}
                        </FormControl>

                        <FormControl className="mb-3" style={{ width: "100%" }}>
                            <TextField
                                label={
                                    language?.source_modal?.app_id ?? "APP ID"
                                }
                                variant="outlined"
                                onChange={(e) =>
                                    handlePlatformInputChange(
                                        platform,
                                        "app_id",
                                        e.target.value,
                                    )
                                }
                                placeholder={
                                    language?.source_modal
                                        ?.please_write_app_id ??
                                    "Please Write APP ID"
                                }
                                value={platformInputs[platform].app_id}
                                required
                                disabled={isSaveLoading}
                                className="mb-2"
                                inputProps={{ "data-cy": "mmp-app-id" }}
                            />
                            {fieldErrors[platform]?.app_id && (
                                <div className="fileUploadStyle">
                                    {fieldErrors[platform].app_id}
                                </div>
                            )}
                        </FormControl>
                    </div>
                ))}

                <FormControl fullWidth>
                    <Tooltip
                        placement="right"
                        title={
                            <Stack>
                                <Box>
                                    <b>API Key Connection:</b> Allows you to
                                    integrate using your Appsflyer API key.
                                </Box>
                                <Box>
                                    <b>Appsflyer Partner Connection:</b> Easily
                                    and quickly connect SignalSight through the
                                    Appsflyer Marketplace.
                                </Box>
                            </Stack>
                        }
                    >
                        <Select
                            value={connectionMethod}
                            onChange={({ target: { value } }) =>
                                setConnectionMethod(value)
                            }
                            className="mb-3"
                            fullWidth
                            label="Connection Method:"
                            disabled={isSaveLoading}
                            data-cy="appsflyer-connection-method"
                        >
                            <MenuItem value={"partnerConnection"}>
                                Appsflyer Partner Connection
                            </MenuItem>
                            <MenuItem value={"apiKeyConnection"}>
                                API Key Connection
                            </MenuItem>
                        </Select>
                    </Tooltip>
                </FormControl>
                {connectionMethod === "apiKeyConnection" ? (
                    <FormControl className="mb-3" style={{ width: "100%" }}>
                        <TextField
                            name="api_key"
                            label={language?.source_modal?.api_key ?? "API Key"}
                            variant="outlined"
                            onChange={(e) => {
                                setApiKey(e.target.value);
                                if (e.target.value) setApiKeyError("");
                            }}
                            placeholder={
                                language?.source_modal?.please_write_api_key ??
                                "Please Write API Key"
                            }
                            value={apiKey}
                            required
                            className="mb-3"
                            disabled={isSaveLoading}
                            inputProps={{ "data-cy": "appsflyer-api-key" }}
                        />
                        {apiKeyError && (
                            <div className="fileUploadStyle">{apiKeyError}</div>
                        )}
                    </FormControl>
                ) : (
                    <a
                        href="https://hq1.appsflyer.com/partner-marketplace/partner/zzgtechltmqk_int-ad_network"
                        target="_blank"
                        rel="noreferrer"
                        style={{ display: "block" }}
                    >
                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={isSaveLoading}
                            fullWidth
                        >
                            <Stack
                                direction="row"
                                gap={1}
                                alignItems="center"
                                paddingY={1}
                            >
                                <div>
                                    <LaunchIcon fontSize={"small"} />
                                </div>
                                <div>
                                    Install SignalSight on Appsflyer Marketplace
                                </div>
                            </Stack>
                        </Button>
                    </a>
                )}
            </Form.Group>

            {!useHostFooter &&
                (props?.btnCheck ? (
                    saveButton
                ) : (
                    <Modal.Footer className="source-modal-footer">
                        <StandartButton
                            variant={ButtonVariant.BLACK_OUTLINED}
                            onClick={handleCloseModal}
                            disabled={isSaveLoading}
                        >
                            {language?.buttons?.close ?? "Close"}
                        </StandartButton>
                        {saveButton}
                    </Modal.Footer>
                ))}
        </div>
    );
};

export default AppsFlyerSource;
